<script setup lang="ts">
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { useStore } from "@/store";
    import { ANNAT_VAL } from "@/tabs/Receptforskrivning/helpers.js";
    import { computed, ref, toRefs } from "vue";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            mallId?: string;
        }>(),
        {}
    );

    const { mallId } = toRefs(props);

    const mall = computed(() => {
        return store.getters["receptforskrivningMall/mallById"]({
            id: mallId?.value,
        });
    });

    const administreringsmetod = ref(mall?.value?.administreringsmetod);
    const administreringsvag = ref(mall?.value?.administreringsvag);
    const administreringsstalle = ref(mall?.value?.administreringsstalle);
    const precisering_administreringsstalle = ref(mall?.value?.precisering_administreringsstalle);
    const medicinteknisk_produkt = ref(mall?.value?.medicinteknisk_produkt);

    defineExpose({
        formListeners: [
            {
                fieldNames: ["administreringsmetod"],
                action: ({ values }) => {
                    administreringsmetod.value = values.administreringsmetod;
                },
            },
            {
                fieldNames: ["administreringsvag"],
                action: ({ values }) => {
                    administreringsvag.value = values.administreringsvag;
                },
            },
            {
                fieldNames: ["administreringsstalle"],
                action: ({ values }) => {
                    administreringsstalle.value = values.administreringsstalle;
                },
            },
            {
                fieldNames: ["precisering_administreringsstalle"],
                action: ({ values }) => {
                    precisering_administreringsstalle.value = values.precisering_administreringsstalle;
                },
            },
            {
                fieldNames: ["medicinteknisk_produkt"],
                action: ({ values }) => {
                    medicinteknisk_produkt.value = values.medicinteknisk_produkt;
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });
</script>

<template>
    <KSection name="dosering" heading="Dosering" :number="5">
        <div class="form-content readable-disabled">
            <KText
                name="dosering"
                class="span-all"
                label="Dosering"
                suffixText="Visas i doseringsanvisning"
                type="textarea"
            />
            <KCheckbox name="vid_behov" label="Vid behov" valueType="boolean" />
            <KCheckbox name="obs_overstiger_maxdosering" label="Obs. överstiger maxdosering" valueType="boolean" />
            <KGroup
                name="administrering"
                label="Administrering"
                class="span-all"
                subText="Välj ett eller flera administreringsval"
            >
                <KCheckbox
                    name="visa_administrering"
                    validationName="Administrering"
                    freeFlow
                    direction="horizontal"
                    class="span-all"
                    :options="[
                        { label: 'Metod', value: 'metod' },
                        { label: 'Väg', value: 'vag' },
                        { label: 'Ställe', value: 'stalle' },
                        { label: 'Medicinteknisk produkt', value: 'medicinteknisk_produkt' },
                    ]"
                />
                <KAutocomplete name="administreringsmetod" :options="[]" label="Administreringsmetod" disabled />
                <KText
                    v-if="administreringsmetod === ANNAT_VAL"
                    name="administreringsmetod_annan"
                    label="Annan metod"
                />
                <KAutocomplete name="administreringsvag" :options="[]" label="Administreringsväg" disabled />
                <KText v-if="administreringsvag === ANNAT_VAL" name="administreringsvag_annan" label="Annan väg" />
                <KAutocomplete name="administreringsstalle" :options="[]" label="Administreringsställe" disabled />
                <KText
                    v-if="administreringsstalle === ANNAT_VAL"
                    name="administreringsstalle_annan"
                    label="Annat ställe"
                />
                <KAutocomplete
                    name="precisering_administreringsstalle"
                    :options="[]"
                    label="Precisering administreringsställe"
                    disabled
                />
                <KText
                    v-if="precisering_administreringsstalle === ANNAT_VAL"
                    name="precisering_administreringsstalle_annan"
                    label="Annan precisering"
                />
                <KAutocomplete name="medicinteknisk_produkt" :options="[]" label="Medicinteknisk produkt" disabled />
                <KText
                    v-if="medicinteknisk_produkt === ANNAT_VAL"
                    name="medicinteknisk_produkt_annan"
                    label="Annan medicinteknisk produkt"
                />
                <KCheckbox name="administrering_all" label="Visa alla administreringsalternativ" valign="center" />
            </KGroup>
            <KText
                name="annan_instruktion"
                label="Annan instruktion"
                type="textarea"
                class="span-two"
                suffixText="Visas i doseringsanvisning"
            />
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    .dosering {
    }
</style>
